import { Component, inject, OnInit, signal, DestroyRef } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgxPaginationModule } from 'ngx-pagination';
import { finalize } from 'rxjs';

import { PageSize } from 'src/app/enumeration/pageSize';
import { UserLoginRole } from 'src/app/enumeration/roles';
import { LuckydrawApiService } from 'src/app/service/luckydraw/luckydraw-api.service';
import { CheckRolesAccessService } from 'src/app/utils/check_roles_access/check-roles-access.service';
import { DateFormatPipe } from 'src/app/utils/date-format.pipe';
import { DownloadService } from 'src/app/utils/download/download.service';
import { ThemeService } from 'src/app/service/theme/theme.service';

// Strict interface modeling to bypass strict template checking errors
export interface LuckyDrawListResponse {
  nextContinuation: number
  pageTotal: number
  allTotal: number
  luckyDrawInfoLst: LuckyDrawInfoLst[]
}

export interface LuckyDrawInfoLst {
  srNo: number
  name: string
  materialName: string
  productionOrderNumber: string
  batchNumer: string
  quantity: number
  qrCodeStartSerialNumber: string
  qrCodeLastSerialNumber: string
  totalPoints?: number
  activate: boolean
  qrCodeEnabled: boolean
  createdTs: string
  updatedTs: string
}
@Component({
  selector: 'app-lucky-draw-list',
  standalone: true,
  imports: [
    CommonModule,
    NgxPaginationModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
    DateFormatPipe,
  ],
  templateUrl: './lucky-draw-list.component.html',
  styleUrls: ['./lucky-draw-list.component.css'],
})
export class LuckyDrawListComponent implements OnInit {
  // Functional Injections Framework 
  private readonly fb = inject(FormBuilder);
  private readonly router = inject(Router);
  private readonly apiService = inject(LuckydrawApiService);
  private readonly checkAccessRoles = inject(CheckRolesAccessService);
  private readonly downloadService = inject(DownloadService);
  private readonly datePipe = inject(DatePipe);
  private readonly themeService = inject(ThemeService);
  private readonly destroyRef = inject(DestroyRef);

  // Link shared layout dark state signal instantly to view canvas parameters
  readonly isDarkMode = this.themeService.isDarkMode;

  // Fully refactored component view data tracking structures using modern signals
  readonly results = signal<LuckyDrawInfoLst[]>([]);
  readonly total = signal<number>(0);
  readonly allTotal = signal<number>(0);
  readonly page = signal<number>(1); // 1-based index template matching parameter
  readonly pageItemsSize = signal<number>(PageSize.LUCKY_DRAW_LIST);

  // Layout Loading Indicators
  readonly loading = signal<boolean>(false);
  readonly apiLoading = signal<boolean>(false);
  readonly apiDownloadLoading = signal<boolean>(false);
  readonly isEnableBtnLoading = signal<boolean[]>([]);

  // Permissions Configurations
  auditAccess = true;
  isEnableDisableAccess = false;

  // Active Query Parameters
  partOfLuckyDrawName = '';
  startDate = '';
  endDate = '';
  selectedName = '';
  downloadFileName = '';

  // Strongly typed form groups
  newDataForm!: FormGroup;
  updateDataForm!: FormGroup;
  mathMin = Math.min;

  constructor() {
    this.initializeValidationForms();
  }

  ngOnInit(): void {
    this.evaluateSecurityPermissions();
    this.listAll();
  }

  private initializeValidationForms(): void {
    const baseFormControls = {
      name: ['', Validators.required],
      materialName: ['', Validators.required],
      productionOrderNumber: ['', Validators.required],
      batchNumer: ['', Validators.required],
      quantity: [''],
      qrCodeStartSerialNumber: ['', [Validators.required, Validators.pattern('^[0-9]*$')]],
      qrCodeLastSerialNumber: ['', [Validators.required, Validators.pattern('^[0-9]*$')]],
    };

    this.newDataForm = this.fb.group(baseFormControls);
    this.updateDataForm = this.fb.group(baseFormControls);
  }

  private evaluateSecurityPermissions(): void {
    this.isEnableDisableAccess = this.checkAccessRoles.check([
      UserLoginRole.SUPER_ADMIN,
      UserLoginRole.LUCKY_DRAW_ADMIN,
    ]);
    this.auditAccess = this.checkAccessRoles.check([UserLoginRole.AUDIT_ADMIN]);
  }

  /**
   * Fetches baseline administrative logs matching un-filtered page indices
   */
  listAll(): void {
    this.loading.set(true);
    const zeroBasedPage = this.page() - 1;

    this.apiService.getLuckyDrawList(zeroBasedPage)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.loading.set(false))
      )
      .subscribe({
        next: (response: LuckyDrawListResponse) => {
          this.total.set(response?.pageTotal || 0);
          this.allTotal.set(response?.allTotal || 0);
          this.results.set(response?.luckyDrawInfoLst || []);
        },
        error: (err: any) => {
          console.error('Un-filtered campaign lookup pipeline error:', err);
          alert(err?.message || err || 'Failed to update campaign tracking records.');
        }
      });
  }

  /**
   * Toggles operational execution parameters on/off for target batch sets
   */
  enabledDisabled(type: any, name: any, index: any): void {
    this.isEnableBtnLoading.update(loaders => {
      const copy = [...loaders];
      copy[index] = true;
      return copy;
    });

    this.apiService.getEnabledDisabled(type, name)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.isEnableBtnLoading.update(loaders => {
            const copy = [...loaders];
            copy[index] = false;
            return copy;
          });
        })
      )
      .subscribe({
        next: () => {
          alert('Campaign state successfully adjusted.');
        },
        error: (err: any) => {
          console.error('Failed to change constraint execution rules:', err);
          alert(err?.message || err || 'Authorization shift exception.');
        },
        complete: () => {
          this.listAll();
        }
      });
  }

  /**
   * Provisions a brand new campaign template directly into active ledgers
   */
  addNewLucky(): void {
    if (this.newDataForm.invalid) return;

    this.apiLoading.set(true);

    this.apiService.getAddNewLuckyDraw(this.newDataForm.value)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.apiLoading.set(false))
      )
      .subscribe({
        next: (v: any) => {
          alert(`Success: Campaign "${v?.name || ''}" [${v?.materialName || ''}] registered.`);
          this.newDataForm.reset();

          // Dismiss active creation modal element safely using clean class definitions
          this.closeModalViaNativeElement('createLuckyDrawModal');
          this.listAll();
        },
        error: (err: any) => {
          console.error('Ingestion profile writing error:', err);
          alert(err?.message || err || 'Failed to initialize campaign entry.');
        }
      });
  }

  /**
   * Dispatches filter operations using part name queries or chronological date sequences
   */
  listAllWithPartOfName(filterPage: number): void {
    if (filterPage === 0) {
      this.page.set(1); // Force navigation index boundaries safely back to step 1
    }

    let startFormatted: string | null = null;
    let endFormatted: string | null = null;

    if (!this.startDate && this.endDate) {
      alert('Please select start date');
      return;
    }
    if (this.startDate && !this.endDate) {
      alert('Please select end date');
      return;
    }

    if (this.startDate && this.endDate) {
      startFormatted = this.datePipe.transform(this.startDate, 'dd/MM/yyyy');
      endFormatted = this.datePipe.transform(this.endDate, 'dd/MM/yyyy');
    }

    this.loading.set(true);
    const zeroBasedPage = this.page() - 1;

    this.apiService.getlistLuckyDrawItemsWithPartOfNameDate(
      startFormatted,
      endFormatted,
      this.partOfLuckyDrawName,
      zeroBasedPage
    )
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.loading.set(false))
      )
      .subscribe({
        next: (response: LuckyDrawListResponse) => {
          this.total.set(response?.pageTotal || 0);
          this.allTotal.set(response?.allTotal || 0);
          this.results.set(response?.luckyDrawInfoLst || []);
        },
        error: (err: any) => {
          console.error('Filtered lookups evaluation error:', err);
          alert(err?.message || err || 'No results discovered matching criteria.');
        }
      });
  }

  /**
   * Flushes active query parameter variables and restores baseline un-filtered matrices
   */
  ResetPage(): void {
    this.startDate = '';
    this.endDate = '';
    this.partOfLuckyDrawName = '';
    this.page.set(1);
    this.listAll();
  }

  private closeModalViaNativeElement(elementId: string): void {
    const targetModalNode = document.getElementById(elementId);
    if (targetModalNode) {
      const bootstrapInstance = (window as any).bootstrap?.Modal?.getInstance(targetModalNode);
      bootstrapInstance?.hide();
    }
  }


  /**
 * Pre-loads a single entity configuration profile to populate update input nodes cleanly
 */
  GetSingleView(name: string): void {
    if (!name) return;

    this.apiLoading.set(true);

    this.apiService.getLuckyDrawInfo(name)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.apiLoading.set(false)) // Unconditionally drops active spinner flags
      )
      .subscribe({
        next: (data: any) => {
          if (!data) return;
          this.selectedName = data.name;

          // Replaced individual control setters with a single immutable, type-safe block patch
          this.updateDataForm.patchValue({
            name: data.name,
            materialName: data.materialName,
            productionOrderNumber: data.productionOrderNumber,
            batchNumer: data.batchNumer,
            quantity: data.quantity || '',
            qrCodeStartSerialNumber: data.qrCodeStartSerialNumber,
            qrCodeLastSerialNumber: data.qrCodeLastSerialNumber,
          });
        },
        error: (err: any) => {
          console.error('Individual node profile parsing exception:', err);
          alert(err?.message || err || 'Failed to synchronize layout data nodes context.');
        }
      });
  }

  /**
   * Commits standalone field updates to an existing campaign record back onto server ledgers
   */
  UpdateData(): void {
    if (this.updateDataForm.invalid) return;

    this.apiLoading.set(true);

    this.apiService.getUpdateLuckyDraw(this.updateDataForm.value)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.apiLoading.set(false))
      )
      .subscribe({
        next: () => {
          alert('Lucky draw successfully updated.');
          this.closeModalViaNativeElement('updateLuckyDrawModal'); // Safe native abstract modal dismissal
          this.listAll(); // Re-sync active listings array dynamically
        },
        error: (err: any) => {
          console.error('Critical execution exception during campaign updates:', err);
          alert(err?.message || err || 'Failed to apply updated specifications.');
        }
      });
  }

  /**
   * Permanently activates a targeted campaign asset block profile within backend structures
   */
  Activate(name: string): void {
    if (!name) return;

    // Inverted logic clean gate: Immediately stops execution if user dismisses native confirmation popup
    if (!confirm('You cannot undo this action after activation. Are you sure?')) {
      return;
    }

    this.apiLoading.set(true);

    this.apiService.getActivateLuckyDraw(name)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.apiLoading.set(false))
      )
      .subscribe({
        next: () => {
          alert('Lucky draw activated successfully.');
          this.listAll();
        },
        error: (err: any) => {
          console.error('Critical hardware checkpoint intercept during activation:', err);
          alert(err?.message || err || 'Activation command protocol failed.');
        }
      });
  }

  /**
   * Adapts pagination event click triggers cleanly across index arrays
   */
  handlePageChange(event: number): void {
    if (!this.loading()) {
      this.page.set(event);

      // Preserves active filter queries across pagination turning scopes dynamically
      if (this.partOfLuckyDrawName || (this.startDate && this.endDate)) {
        this.listAllWithPartOfName(event);
      } else {
        this.listAll();
      }
    }
  }

  /**
 * Downloads matching reporting statements directly down to local disk storage
 */
  downloadFile(): void {
    let startFormatted: string | null = null;
    let endFormatted: string | null = null;

    // 1. Unified date validation boundaries check checks
    if (!this.startDate && this.endDate) {
      alert('Please select start date');
      return;
    }
    if (this.startDate && !this.endDate) {
      alert('Please select end date');
      return;
    }

    if (this.startDate && this.endDate) {
      startFormatted = this.datePipe.transform(this.startDate, 'dd/MM/yyyy');
      endFormatted = this.datePipe.transform(this.endDate, 'dd/MM/yyyy');
    }

    if (!this.downloadFileName) {
      alert('Please enter a target file name before exporting records.');
      return;
    }

    // 2. Safety extension sanitation enforcement pattern
    let formattedFileName = this.downloadFileName;
    if (!formattedFileName.toLowerCase().endsWith('.csv')) {
      formattedFileName += '.csv';
    }

    this.apiDownloadLoading.set(true);

    this.apiService.getDownloadFile(
      formattedFileName,
      startFormatted,
      endFormatted,
      this.partOfLuckyDrawName
    )
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.apiDownloadLoading.set(false))
      )
      .subscribe({
        next: (blobPayload: any) => {
          // 3. Process local binary file stream download
          this.downloadService.downloadFileInLocal(blobPayload, formattedFileName);

          // 4. SUCCESS-ONLY CLEANUP: Only flush fields and hide modal upon a successful extraction
          this.downloadFileName = '';
          this.closeModalViaNativeElement('downloadCSVModal');
        },
        error: (err: any) => {
          // Keep production visibility active for debugging infrastructure failures
          console.error('Campaign CSV download pipeline failure:', err);
          alert(err?.message || err || 'Failed to stream target reporting records.');
        }
      });
  }

}
